import { MaxFileSizeIndicator } from "@/components/common/Uploader/MaxFileSizeIndicator/MaxFileSizeIndicator";
import { getSupportedExtensions } from "@/store/features/bulkResize/utils/getSupportedExtensions";
import { useGoogleDriveUpload } from "client/hooks/upload/useGoogleDriveUpload";
import { AddIcon } from "components/common/Icons/AddIcon";
import { ArrowIcon } from "components/common/Icons/ArrowIcon";
import { List } from "components/common/List/List";
import { Popup } from "components/common/Popup/Popup";
import { Translate } from "components/common/Translate/Translate";
import { DeviceUpload } from "components/common/Upload/DeviceUpload/DeviceUpload";
import { DropboxUpload } from "components/common/Upload/DropboxUpload/DropboxUpload";
import { GoogleDriveAccessErrorModal } from "components/common/Upload/GoogleDriveUpload/GoogleDriveAccessErrorModal/GoogleDriveAccessErrorModal";
import { GoogleDriveUpload } from "components/common/Upload/GoogleDriveUpload/GoogleDriveUpload";
import { UrlUpload } from "components/common/Upload/UrlUpload/UrlUpload";
import { useRouter } from "next/router";
import { useNoImage } from "./useNoImage";

export const NoImage = () => {
    const { images, isOpen, toggle, close, handleFile, handleDeviceInputClick, isDragging } = useNoImage();
    const router = useRouter();
    const supportedExtensions = getSupportedExtensions(router.asPath);
    const {
        handleClick: handleGoogleDriveUploadClick,
        accessError,
        setAccessError,
        isLoading: isGoogleDriveLoading,
    } = useGoogleDriveUpload(handleFile, { allowMultiple: true, supportedExtensions }, isOpen);

    if (images.length > 0) return null;

    return (
        <div className="h-full p-4 w-full">
            <GoogleDriveAccessErrorModal
                isOpen={accessError}
                close={() => setAccessError(false)}
                handleRetry={handleGoogleDriveUploadClick}
            />
            <div className="w-full flex flex-col items-center justify-center h-full bg-irGray-200 dark:bg-darkSurface-200 border border-irGray-400 dark:border-darkSurface-400 border-dashed py-16 lg:py-48">
                {isDragging ? (
                    <div className="flex flex-col items-center gap-4">
                        <AddIcon width={30} height={30} className="fill-black dark:fill-darkSurface-600" />
                        <div className="first-letter:capitalize text-irGray-600 text-xl text-center">
                            <Translate keyName="drop-images" />
                        </div>
                    </div>
                ) : (
                    <>
                        <Popup
                            isOpen={isOpen}
                            close={close}
                            content={
                                <div className="absolute flex flex-col -translate-y-4 top-full left-0 right-0 bg-white dark:bg-darkSurface-200 border border-irGray-400 dark:border-darkSurface-100 rounded-xl text-black dark:text-white overflow-hidden">
                                    <List
                                        callback={() => {}}
                                        items={{
                                            from_device: (
                                                <DeviceUpload
                                                    handleFile={handleFile}
                                                    options={{ allowMultiple: true, supportedExtensions }}
                                                    htmlFileInputId="bulk-resizer-input"
                                                />
                                            ),
                                            dropbox: (
                                                <DropboxUpload
                                                    handleFile={handleFile}
                                                    options={{ allowMultiple: true, supportedExtensions }}
                                                    isOpen={isOpen}
                                                />
                                            ),
                                            google_drive: (
                                                <GoogleDriveUpload
                                                    options={{
                                                        allowMultiple: true,
                                                        onClick: handleGoogleDriveUploadClick,
                                                        supportedExtensions,
                                                    }}
                                                    isLoading={isGoogleDriveLoading}
                                                />
                                            ),
                                            url_upload: (
                                                <UrlUpload handleFile={handleFile} options={{ supportedExtensions }} />
                                            ),
                                        }}
                                    />
                                </div>
                            }
                        >
                            <div className="cursor-pointer flex rounded-xl overflow-hidden bg-irBlue-200 dark:bg-darkPrimary-100 mb-8">
                                <div
                                    className="flex items-center justify-center gap-4 px-6 py-4 hover:bg-irBlue-400 dark:hover:bg-darkPrimary-200"
                                    onClick={handleDeviceInputClick}
                                >
                                    <AddIcon width={14} height={14} fill="white" />
                                    <div className="capitalize font-semibold text-white text-lg">
                                        <Translate keyName="common:upload-images" />
                                    </div>
                                </div>
                                <div
                                    className={`flex items-center px-4 border-l border-l-white ${
                                        isOpen && "bg-irBlue-400 dark:bg-darkPrimary-200"
                                    } hover:bg-irBlue-400 dark:hover:bg-darkPrimary-200`}
                                    onClick={toggle}
                                >
                                    {isOpen ? (
                                        <div className="rotate-180">
                                            <ArrowIcon width={14} height={14} />
                                        </div>
                                    ) : (
                                        <ArrowIcon width={14} height={14} stroke={"white"} />
                                    )}
                                </div>
                            </div>
                        </Popup>
                        <div className="first-letter:capitalize text-irGray-600 dark:text-darkSurface-600 text-xl text-center mx-4">
                            <Translate keyName="drag-or-click" />
                        </div>
                        <div className="mt-2 text-sm dark:text-irGray-300">
                            <MaxFileSizeIndicator />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
